class SearchDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get shouldAppendToBody() {
    return false;
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}

customElements.define('search-drawer', SearchDrawer);


class SearchTyped extends HTMLElement {
  constructor() {
    super();
    Motion.inView(this, this.init.bind(this));
  }

  get startDelay() {
    return this.hasAttribute('data-delay')
      ? parseFloat(this.getAttribute('data-delay'))
      : 0;
  }

  async init() {
    this.insertCursor();

    await this.start(
      this.getAttribute('data-first-text'),
      this.startDelay
    );

    setTimeout(async () => {
      await this.reset();

      await this.start(
        this.getAttribute('data-last-text'),
        0
      );
    }, 600);
  }

  async start(text, delay) {
    this.innerHTML = text;

    await Motion.animate(
      this,
      { width: [0, `${this.scrollWidth}px`] },
      { duration: 1, delay }
    ).finished;

    this.cursor.classList.add('blink');
  }

  async reset() {
    this.cursor.classList.remove('blink');

    await Motion.animate(
      this,
      { width: 0 },
      { duration: 0.25 }
    ).finished;
  }

  insertCursor() {
    if (this.cursor) return;

    this.cursor = document.createElement('span');
    this.cursor.className = 'typed-cursor';
    this.cursor.setAttribute('aria-hidden', true);
    this.cursor.innerHTML = '|';

    this.parentElement.insertBefore(
      this.cursor,
      this.nextSibling
    );
  }
}

customElements.define('search-typed', SearchTyped);


class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();

    this.cachedMap = new Map();
    this.bilingualCache = new Map();
    this.localizedProductCache = new Map();

    this.abortController = null;
    this.requestSequence = 0;

    this.focusElement = this.input;

    this.resetButton.addEventListener(
      'click',
      this.clear.bind(this)
    );

    this.input.addEventListener(
      'click',
      this.hideTypewriter.bind(this)
    );

    this.input.addEventListener(
      'input',
      theme.utils.debounce(
        this.onChange.bind(this),
        250
      )
    );

    this.input.addEventListener(
      'focus',
      this.onFocus.bind(this)
    );

    this.injectBilingualStyles();
  }


  get input() {
    return this.querySelector('input[type="search"]');
  }

  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get typewriter() {
    return this.input.previousElementSibling;
  }

  get resultsElement() {
    const id =
      'PredictiveSearchResults-' +
      theme.utils.sectionId(this);

    return document.getElementById(id);
  }

  get currentLanguage() {
    return (
      document.documentElement.lang ||
      'en'
    )
      .toLowerCase()
      .split('-')[0];
  }

  get isArabicStorefront() {
    return this.currentLanguage === 'ar';
  }

  containsArabic(text) {
    return /[\u0600-\u06FF]/.test(text);
  }

  shouldUseBilingualSearch() {
    return (
      this.isArabicStorefront ||
      this.containsArabic(this.getQuery())
    );
  }

  getLocaleRoot() {
    if (
      window.Shopify &&
      Shopify.routes &&
      Shopify.routes.root
    ) {
      return Shopify.routes.root.replace(/\/+$/, '');
    }

    return this.isArabicStorefront
      ? '/ar'
      : '';
  }

  getResultLimit() {
    return this.hasAttribute('data-limit')
      ? parseInt(this.getAttribute('data-limit'), 10)
      : 3;
  }


  hideTypewriter() {
    if (
      this.typewriter &&
      !this.typewriter.hasAttribute('hidden')
    ) {
      this.typewriter.setAttribute('hidden', '');
    }
  }

  onFocus(event) {
    if (!this.closest('.collection')) {
      return;
    }

    document.body.classList.add(
      'predictive-search-open'
    );

    if (this.getQuery().length === 0) {
      return;
    }

    if (this.shouldUseBilingualSearch()) {
      this.renderBilingualSearch();
      return;
    }

    this.renderSection(
      this.buildUrl().toString(),
      event
    );
  }

  clear(event = null) {
    if (event) {
      event.preventDefault();
    }

    this.requestSequence++;

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this.input.value = '';
    this.input.focus();

    this.removeAttribute('results');
    this.removeAttribute('loading');

    if (this.resultsElement) {
      this.resultsElement.innerHTML = '';
    }
  }

  getQuery() {
    return this.input.value.trim();
  }


  /*
   * =========================================================
   * ORIGINAL SHOPIFY PREDICTIVE SEARCH
   * Fast path for English site + English query.
   * =========================================================
   */

  buildUrl() {
    const url = new URL(
      `${theme.routes.shop_url}${theme.routes.predictive_search_url}`
    );

    url.searchParams.set(
      'q',
      this.getQuery()
    );

    url.searchParams.set(
      'resources[limit]',
      this.getResultLimit()
    );

    url.searchParams.set(
      'resources[limit_scope]',
      'each'
    );

    url.searchParams.set(
      'section_id',
      theme.utils.sectionId(this)
    );

    return url;
  }

  onChange() {
    const query = this.getQuery();

    if (!query) {
      this.clear();
      return;
    }

    this.hideTypewriter();

    if (this.shouldUseBilingualSearch()) {
      this.renderBilingualSearch();
      return;
    }

    this.renderSection(
      this.buildUrl().toString()
    );
  }

  renderSection(url) {
    if (this.cachedMap.has(url)) {
      this.renderSectionFromCache(url);
      return;
    }

    this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText =
      this.cachedMap.get(url);

    this.renderSearchResults(responseText);

    this.removeAttribute('loading');
    this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    if (this.abortController) {
      this.abortController.abort();
    }

    const requestId =
      ++this.requestSequence;

    this.abortController =
      new AbortController();

    const signal =
      this.abortController.signal;

    this.setAttribute('loading', '');

    fetch(url, { signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Search failed: ${response.status}`
          );
        }

        return response.text();
      })
      .then((responseText) => {
        if (
          signal.aborted ||
          requestId !== this.requestSequence
        ) {
          return;
        }

        this.renderSearchResults(
          responseText
        );

        this.cachedMap.set(
          url,
          responseText
        );

        this.removeAttribute('loading');
        this.setAttribute('results', '');
      })
      .catch((error) => {
        if (
          error.name === 'AbortError' ||
          requestId !== this.requestSequence
        ) {
          return;
        }

        this.removeAttribute('loading');

        console.error(
          'Predictive search error:',
          error
        );
      });
  }

  renderSearchResults(responseText) {
    const id =
      'PredictiveSearchResults-' +
      theme.utils.sectionId(this);

    const target =
      document.getElementById(id);

    if (!target) {
      return;
    }

    const parsed =
      new DOMParser().parseFromString(
        responseText,
        'text/html'
      );

    const results =
      parsed.getElementById(id);

    if (!results) {
      return;
    }

    target.innerHTML =
      results.innerHTML;
  }


  /*
   * =========================================================
   * iTECH FAST BILINGUAL SEARCH
   * =========================================================
   *
   * OLD METHOD:
   *
   * English Search + Arabic Search
   * at the same time, every search.
   *
   * NEW METHOD:
   *
   * English typed:
   * English search first.
   * Arabic only if nothing found.
   *
   * Arabic typed:
   * Arabic search first.
   * English only if nothing found.
   *
   * This removes unnecessary requests.
   * =========================================================
   */

  async renderBilingualSearch() {
    const query = this.getQuery();

    if (!query) {
      this.clear();
      return;
    }

    const cacheKey =
      `${this.currentLanguage}::${query.toLowerCase()}`;

    if (
      this.bilingualCache.has(cacheKey)
    ) {
      if (this.resultsElement) {
        this.resultsElement.innerHTML =
          this.bilingualCache.get(cacheKey);
      }

      this.removeAttribute('loading');
      this.setAttribute('results', '');

      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    const requestId =
      ++this.requestSequence;

    this.abortController =
      new AbortController();

    const signal =
      this.abortController.signal;

    this.setAttribute('loading', '');

    try {
      /*
       * STEP 1:
       * Search ONLY the language the customer typed.
       */

      let handles =
        await this.searchPrimaryLanguage(
          query,
          signal
        );

      if (
        signal.aborted ||
        requestId !== this.requestSequence
      ) {
        return;
      }

      /*
       * STEP 2:
       * Only use the other language if we found nothing.
       */

      if (handles.length === 0) {
        handles =
          await this.searchFallbackLanguage(
            query,
            signal
          );
      }

      if (
        signal.aborted ||
        requestId !== this.requestSequence
      ) {
        return;
      }

      handles = [
        ...new Set(
          handles.filter(Boolean)
        )
      ];

      const limit =
        this.getResultLimit();

      handles =
        handles.slice(
          0,
          Math.max(limit, 3)
        );

      /*
       * STEP 3:
       * Load those matched products in CURRENT site language.
       *
       * Arabic site -> Arabic title.
       * English site -> English title.
       */

      const localizedProducts =
        await Promise.all(
          handles.map((handle) =>
            this.fetchLocalizedProduct(
              handle,
              signal
            ).catch(() => null)
          )
        );

      if (
        signal.aborted ||
        requestId !== this.requestSequence
      ) {
        return;
      }

      const products =
        localizedProducts.filter(Boolean);

      const html =
        this.buildBilingualResultsHTML(
          products
        );

      if (this.resultsElement) {
        this.resultsElement.innerHTML =
          html;
      }

      this.bilingualCache.set(
        cacheKey,
        html
      );

      this.removeAttribute('loading');
      this.setAttribute('results', '');
    } catch (error) {
      if (
        error.name === 'AbortError' ||
        requestId !== this.requestSequence
      ) {
        return;
      }

      console.error(
        'iTech bilingual search error:',
        error
      );

      this.removeAttribute('loading');

      if (this.resultsElement) {
        this.resultsElement.innerHTML =
          this.buildNoResultsHTML();
      }

      this.setAttribute('results', '');
    }
  }


  async searchPrimaryLanguage(
    query,
    signal
  ) {
    if (this.containsArabic(query)) {
      /*
       * User typed Arabic.
       * Search Arabic first.
       */

      return this.searchArabicProducts(
        query,
        signal
      );
    }

    /*
     * User typed English / Latin.
     * Search English first.
     */

    return this.searchEnglishProducts(
      query,
      signal
    );
  }


  async searchFallbackLanguage(
    query,
    signal
  ) {
    if (this.containsArabic(query)) {
      /*
       * Arabic returned nothing.
       * Try English index.
       */

      return this.searchEnglishProducts(
        query,
        signal
      );
    }

    /*
     * English returned nothing.
     * Try Arabic index.
     */

    return this.searchArabicProducts(
      query,
      signal
    );
  }


  /*
   * =========================================================
   * FAST ENGLISH SEARCH
   * Shopify lightweight JSON endpoint.
   * =========================================================
   */

  async searchEnglishProducts(
    query,
    signal
  ) {
    const url = new URL(
      '/search/suggest.json',
      window.location.origin
    );

    url.searchParams.set(
      'q',
      query
    );

    url.searchParams.set(
      'resources[type]',
      'product'
    );

    url.searchParams.set(
      'resources[limit]',
      Math.max(
        this.getResultLimit(),
        6
      )
    );

    url.searchParams.set(
      'resources[limit_scope]',
      'each'
    );

    const response =
      await fetch(
        url.toString(),
        {
          signal,
          headers: {
            Accept: 'application/json'
          }
        }
      );

    if (!response.ok) {
      return [];
    }

    const data =
      await response.json();

    const products =
      data?.resources?.results?.products;

    if (!Array.isArray(products)) {
      return [];
    }

    const handles = [];

    for (const product of products) {
      const handle =
        this.extractProductHandle(
          product.url || ''
        );

      if (
        handle &&
        !handles.includes(handle)
      ) {
        handles.push(handle);
      }
    }

    return handles;
  }


  /*
   * =========================================================
   * ARABIC SEARCH
   * Uses Shopify normal Arabic search page.
   * =========================================================
   */

  async searchArabicProducts(
    query,
    signal
  ) {
    const url = new URL(
      '/ar/search',
      window.location.origin
    );

    url.searchParams.set(
      'q',
      query
    );

    url.searchParams.set(
      'type',
      'product'
    );

    url.searchParams.set(
      'options[prefix]',
      'last'
    );

    const response =
      await fetch(
        url.toString(),
        {
          signal,
          headers: {
            Accept: 'text/html'
          }
        }
      );

    if (!response.ok) {
      return [];
    }

    const html =
      await response.text();

    return this.extractProductHandlesFromHTML(
      html
    );
  }


  extractProductHandlesFromHTML(
    html
  ) {
    const doc =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    /*
     * Do not read Cart Drawer / Recently Viewed links.
     * Search only the actual page main area.
     */

    const main =
      doc.querySelector('#MainContent') ||
      doc.querySelector('main');

    if (!main) {
      return [];
    }

    const links =
      main.querySelectorAll(
        'a[href*="/products/"]'
      );

    const handles = [];

    for (const link of links) {
      const handle =
        this.extractProductHandle(
          link.getAttribute('href')
        );

      if (
        handle &&
        !handles.includes(handle)
      ) {
        handles.push(handle);
      }

      /*
       * We don't need to parse hundreds of links.
       */

      if (
        handles.length >=
        Math.max(
          this.getResultLimit(),
          6
        )
      ) {
        break;
      }
    }

    return handles;
  }


  extractProductHandle(url) {
    if (!url) {
      return null;
    }

    try {
      const parsed =
        new URL(
          url,
          window.location.origin
        );

      const match =
        parsed.pathname.match(
          /\/products\/([^/?#]+)/
        );

      if (!match) {
        return null;
      }

      return decodeURIComponent(
        match[1]
      );
    } catch (error) {
      return null;
    }
  }


  /*
   * =========================================================
   * LOAD PRODUCT IN CURRENT LANGUAGE
   * =========================================================
   */

  async fetchLocalizedProduct(
    handle,
    signal
  ) {
    const localeRoot =
      this.getLocaleRoot();

    const cacheKey =
      `${localeRoot || '/'}::${handle}`;

    if (
      this.localizedProductCache.has(
        cacheKey
      )
    ) {
      return this.localizedProductCache.get(
        cacheKey
      );
    }

    const url =
      `${localeRoot}/products/${encodeURIComponent(
        handle
      )}.js`;

    const response =
      await fetch(
        url,
        {
          signal,
          headers: {
            Accept: 'application/json'
          }
        }
      );

    if (!response.ok) {
      throw new Error(
        `Unable to load product: ${handle}`
      );
    }

    const product =
      await response.json();

    product._itechHandle =
      handle;

    this.localizedProductCache.set(
      cacheKey,
      product
    );

    return product;
  }


  /*
   * =========================================================
   * RESULT HTML
   * =========================================================
   */

  buildBilingualResultsHTML(
    products
  ) {
    if (
      !products ||
      products.length === 0
    ) {
      return this.buildNoResultsHTML();
    }

    const localeRoot =
      this.getLocaleRoot();

    const rows =
      products.map((product) => {
        const handle =
          product._itechHandle ||
          product.handle;

        const productUrl =
          `${localeRoot}/products/${encodeURIComponent(
            handle
          )}`;

        const title =
          product.title || '';

        const vendor =
          product.vendor || '';

        const image =
          this.getProductImage(
            product
          );

        const price =
          Number(
            product.price || 0
          );

        const comparePrice =
          Number(
            product.compare_at_price ||
            0
          );

        const priceLabel =
          product.price_varies
            ? this.isArabicStorefront
              ? `من ${this.formatMoney(price)}`
              : `From ${this.formatMoney(price)}`
            : this.formatMoney(price);

        const imageHTML =
          image
            ? `
              <div class="itech-bilingual-search__image-wrap">
                <img
                  class="itech-bilingual-search__image"
                  src="${this.escapeHTML(image)}"
                  alt="${this.escapeHTML(title)}"
                  loading="lazy"
                  width="82"
                  height="82"
                >
              </div>
            `
            : `
              <div class="itech-bilingual-search__image-wrap itech-bilingual-search__image-wrap--empty"></div>
            `;

        const compareHTML =
          comparePrice > price
            ? `
              <span class="itech-bilingual-search__compare-price">
                ${this.escapeHTML(
                  this.formatMoney(comparePrice)
                )}
              </span>
            `
            : '';

        return `
          <a
            href="${this.escapeHTML(productUrl)}"
            class="itech-bilingual-search__item"
            role="option"
          >

            ${imageHTML}

            <div class="itech-bilingual-search__content">

              ${
                vendor
                  ? `
                    <div class="itech-bilingual-search__vendor">
                      ${this.escapeHTML(vendor)}
                    </div>
                  `
                  : ''
              }

              <div class="itech-bilingual-search__title">
                ${this.escapeHTML(title)}
              </div>

              <div class="itech-bilingual-search__prices">

                <span class="itech-bilingual-search__price">
                  ${this.escapeHTML(priceLabel)}
                </span>

                ${compareHTML}

              </div>

            </div>

          </a>
        `;
      }).join('');

    return `
      <div
        class="itech-bilingual-search"
        dir="${this.isArabicStorefront ? 'rtl' : 'ltr'}"
      >
        ${rows}
      </div>
    `;
  }


  buildNoResultsHTML() {
    const text =
      this.isArabicStorefront
        ? 'لم يتم العثور على منتجات مطابقة لبحثك.'
        : 'No products found for your search.';

    return `
      <div
        class="itech-bilingual-search__empty"
        dir="${this.isArabicStorefront ? 'rtl' : 'ltr'}"
      >
        ${this.escapeHTML(text)}
      </div>
    `;
  }


  getProductImage(product) {
    let image =
      product.featured_image;

    if (
      image &&
      typeof image === 'object'
    ) {
      image =
        image.src ||
        image.url ||
        '';
    }

    if (
      !image &&
      Array.isArray(product.images) &&
      product.images.length
    ) {
      image =
        product.images[0];

      if (
        image &&
        typeof image === 'object'
      ) {
        image =
          image.src ||
          image.url ||
          '';
      }
    }

    if (
      typeof image !== 'string'
    ) {
      return '';
    }

    if (image.startsWith('//')) {
      return (
        window.location.protocol +
        image
      );
    }

    return image;
  }


  formatMoney(cents) {
    const amount =
      Number(cents || 0) /
      100;

    const currency =
      window.Shopify &&
      Shopify.currency &&
      Shopify.currency.active
        ? Shopify.currency.active
        : 'EGP';

    return `${currency} ${amount.toLocaleString(
      'en-US',
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    )}`;
  }


  escapeHTML(value) {
    return String(
      value ?? ''
    )
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }


  injectBilingualStyles() {
    if (
      document.getElementById(
        'itech-bilingual-search-styles'
      )
    ) {
      return;
    }

    const style =
      document.createElement('style');

    style.id =
      'itech-bilingual-search-styles';

    style.textContent = `
      .itech-bilingual-search {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 0;
        padding: 0;
        margin: 0;
      }

      .itech-bilingual-search__item {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 16px 0;
        color: inherit;
        text-decoration: none;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);
        transition:
          opacity 0.2s ease,
          transform 0.2s ease;
      }

      .itech-bilingual-search__item:first-child {
        padding-top: 0;
      }

      .itech-bilingual-search__item:last-child {
        border-bottom: 0;
      }

      .itech-bilingual-search__item:hover {
        opacity: 0.78;
      }

      .itech-bilingual-search__image-wrap {
        width: 82px;
        height: 82px;
        min-width: 82px;
        border-radius: 14px;
        overflow: hidden;
        background: #f5f5f7;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .itech-bilingual-search__image-wrap--empty {
        background: #f5f5f7;
      }

      .itech-bilingual-search__image {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
      }

      .itech-bilingual-search__content {
        min-width: 0;
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .itech-bilingual-search[dir="rtl"]
      .itech-bilingual-search__content {
        text-align: right;
      }

      .itech-bilingual-search[dir="ltr"]
      .itech-bilingual-search__content {
        text-align: left;
      }

      .itech-bilingual-search__vendor {
        color: #86868b;
        font-size: 11px;
        line-height: 1.3;
        font-weight: 500;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }

      .itech-bilingual-search__title {
        color: #1d1d1f;
        font-size: 15px;
        line-height: 1.4;
        font-weight: 600;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      .itech-bilingual-search__prices {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 7px;
        margin-top: 2px;
      }

      .itech-bilingual-search__price {
        color: #1d1d1f;
        font-size: 13px;
        line-height: 1.4;
        font-weight: 500;
        direction: ltr;
        unicode-bidi: isolate;
      }

      .itech-bilingual-search__compare-price {
        color: #86868b;
        font-size: 12px;
        line-height: 1.4;
        text-decoration: line-through;
        direction: ltr;
        unicode-bidi: isolate;
      }

      .itech-bilingual-search__empty {
        width: 100%;
        padding: 24px 0;
        color: #6e6e73;
        font-size: 14px;
        line-height: 1.6;
      }

      .itech-bilingual-search__empty[dir="rtl"] {
        text-align: right;
      }

      .itech-bilingual-search__empty[dir="ltr"] {
        text-align: left;
      }

      @media screen and (max-width: 749px) {
        .itech-bilingual-search__item {
          gap: 13px;
          padding: 14px 0;
        }

        .itech-bilingual-search__image-wrap {
          width: 72px;
          height: 72px;
          min-width: 72px;
          border-radius: 12px;
        }

        .itech-bilingual-search__title {
          font-size: 14px;
        }

        .itech-bilingual-search__price {
          font-size: 12px;
        }
      }
    `;

    document.head.appendChild(style);
  }
}

customElements.define(
  'predictive-search',
  PredictiveSearch,
  { extends: 'form' }
);


class PredictiveSearchOverlay extends OverlayElement {
  constructor() {
    super();

    this.addEventListener(
      'click',
      this.onClick
    );
  }

  onClick() {
    setTimeout(() => {
      document.body.classList.remove(
        'predictive-search-open'
      );
    });
  }
}

customElements.define(
  'predictive-search-overlay',
  PredictiveSearchOverlay
);